// src/utils/chartUtils.js
// Вспомогательные функции для работы с графиками

export const createFormatters = () => {
  const fmtCompact = (n) =>
    new Intl.NumberFormat("uz-UZ", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);

  const fmtNum = (n) =>
    new Intl.NumberFormat("uz-UZ", { maximumFractionDigits: 2 }).format(n);

  return { fmtCompact, fmtNum };
};

export const easeOutC = (t) => 1 - Math.pow(1 - t, 3);
export const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export function niceStep(max, target = 6) {
  if (max <= 0) return 1;
  const rough = max / target,
    pow = 10 ** Math.floor(Math.log10(rough)),
    base = rough / pow;
  return (base <= 1 ? 1 : base <= 2 ? 2 : base <= 5 ? 5 : 10) * pow;
}

export function buildPalette(n) {
  const cs = getComputedStyle(document.documentElement);
  const baseHueRaw = cs.getPropertyValue("--baseHue") || "220";
  const baseHue = +baseHueRaw.trim();
  const colors = [],
    hovers = [];
  for (let i = 0; i < n; i++) {
    const l = 42 + (72 - 42) * (i / Math.max(1, n - 1));
    const s = 70;
    colors.push(`hsl(${baseHue} ${s}% ${l}%)`);
    const l2 = Math.min(90, l + 6),
      s2 = Math.min(90, s + 8);
    hovers.push(`hsl(${baseHue} ${s2}% ${l2}%)`);
  }
  return { colors, hovers };
}

export function dateFormatter(dates) {
  if (!dates.length) return (d) => "";
  const min = new Date(Math.min(...dates.map((d) => +d)));
  const max = new Date(Math.max(...dates.map((d) => +d)));
  const span = max - min;
  let opts;
  if (span <= 2 * 86400000) opts = { hour: "2-digit", minute: "2-digit" };
  else if (span <= 60 * 86400000) opts = { day: "2-digit", month: "short" };
  else if (span <= 370 * 86400000) opts = { month: "short", year: "numeric" };
  else opts = { year: "numeric" };
  const fmt = new Intl.DateTimeFormat("uz-UZ", opts);
  return (d) => fmt.format(d);
}

export function positionTooltip(
  tooltip,
  chartEl,
  clientX,
  clientY,
  placement = "right",
  gap = 12
) {
  const r = chartEl.getBoundingClientRect();
  const tw = tooltip.offsetWidth || 0;
  const th = tooltip.offsetHeight || 0;
  let left = clientX - r.left;
  let top = clientY - r.top;
  if (placement === "right") {
    left += gap;
    top -= th / 2;
  } else if (placement === "left") {
    left -= tw + gap;
    top -= th / 2;
  } else if (placement === "top") {
    left -= tw / 2;
    top -= th + gap;
  } else {
    left -= tw / 2;
    top += gap;
  }
  left = clamp(left, 8, r.width - tw - 8);
  top = clamp(top, 8, r.height - th - 8);
  tooltip.style.left = left + "px";
  tooltip.style.top = top + "px";
}

export function buildDailySeries(rawRows, { fromMonth = null } = {}) {
  const rows = (rawRows || [])
    .map((d) => ({
      t: new Date(d.t),
      y: +d.y,
    }))
    .sort((a, b) => a.t - b.t);

  if (!rows.length) return [];

  const first = rows[0].t;
  const last = rows[rows.length - 1].t;

  let start = new Date(first);
  if (fromMonth !== null) {
    const desired = new Date(first.getFullYear(), fromMonth, 1);
    if (desired <= last) start = desired;
  }

  const map = new Map(
    rows.map((r) => [r.t.toISOString().slice(0, 10), r.y])
  );

  const result = [];
  const cur = new Date(start);

  while (cur <= last) {
    const key = cur.toISOString().slice(0, 10);
    const val = map.has(key) ? map.get(key) : 0;
    result.push({ t: key, y: val });
    cur.setDate(cur.getDate() + 1);
  }

  return result;
}

// SVG хелперы
export const createSVGElement = (tagName) =>
  document.createElementNS("http://www.w3.org/2000/svg", tagName);

export function createSVGGroup(parent, transform = null, cls = null, root) {
  const g = createSVGElement("g");
  if (transform) g.setAttribute("transform", transform);
  if (cls) g.setAttribute("class", cls);
  (parent || root.querySelector("svg")).appendChild(g);
  return g;
}

export function createSVGLine(parent, x1, y1, x2, y2, cls, root) {
  const l = createSVGElement("line");
  l.setAttribute("x1", x1);
  l.setAttribute("y1", y1);
  l.setAttribute("x2", x2);
  l.setAttribute("y2", y2);
  if (cls) l.setAttribute("class", cls);
  (parent || root.querySelector("svg")).appendChild(l);
  return l;
}

export function createSVGText(parent, str, x, y, attrs = {}, root) {
  const el = createSVGElement("text");
  el.textContent = str;
  el.setAttribute("x", x);
  el.setAttribute("y", y);
  for (const k in attrs) el.setAttribute(k, attrs[k]);
  (parent || root.querySelector("svg")).appendChild(el);
  return el;
}

export function pathCatmullRom(points, tension = 0.2) {
  if (points.length < 2) return "";
  const d = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    if (i === 0) {
      d.push(`M ${p.x} ${p.y}`);
      continue;
    }
    const p0 = points[i - 2] || points[i - 1];
    const p1 = points[i - 1];
    const p2 = points[i];
    const p3 = points[i + 1] || points[i];
    const t = tension;
    const c1x = p1.x + (p2.x - p0.x) * t;
    const c1y = p1.y + (p2.y - p0.y) * t;
    const c2x = p2.x - (p3.x - p1.x) * t;
    const c2y = p2.y - (p3.y - p1.y) * t;
    d.push(`C ${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`);
  }
  return d.join(" ");
}
